/**
 * Guardian account profile: identity, tier, and remaining quotas.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { fetchGuardianMe, updateGuardianProfile } from '../api/guardianApi';
import { validateProfileName, NAME_MAX } from '../utils/profileValidation';
import AccountDataSection from './AccountDataSection';
import GuardianStyleSection from './GuardianStyleSection';

function formatTier(tier) {
  if (!tier) return '—';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {object|null} props.user - Cached guardian user from auth
 * @param {(user: object, entitlements: object) => void} [props.onProfileLoaded]
 * @param {() => void} props.onBack
 * @param {() => void} [props.onAccountDeleted] - Signs out after deletion (audit P-4).
 */
export default function GuardianProfile({
  authToken,
  user,
  onProfileLoaded,
  onBack,
  onAccountDeleted,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileUser, setProfileUser] = useState(user);
  const [entitlements, setEntitlements] = useState(null);

  // Editable display name (audit P-18). Email and phone stay read-only.
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Keep latest props in refs so a successful /me refresh can update parent
  // state without putting `user` / `onProfileLoaded` in the fetch effect deps
  // (that loop: load → setGuardianUser → new user prop → load again → 429).
  const userRef = useRef(user);
  const onProfileLoadedRef = useRef(onProfileLoaded);
  userRef.current = user;
  onProfileLoadedRef.current = onProfileLoaded;

  const load = useCallback(async () => {
    if (!authToken) {
      setError('Sign in to view your profile.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await fetchGuardianMe(authToken);
    if (!res.success) {
      setError(res.message || 'Failed to load profile');
      setLoading(false);
      return;
    }
    const nextUser = res.data?.user || userRef.current;
    const nextEntitlements = res.data?.entitlements || null;
    setProfileUser(nextUser);
    setEntitlements(nextEntitlements);
    onProfileLoadedRef.current?.(nextUser, nextEntitlements);
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const fullName = [profileUser?.firstName, profileUser?.lastName]
    .filter(Boolean)
    .join(' ') || '—';

  const startEdit = () => {
    setFirstName(profileUser?.firstName || '');
    setLastName(profileUser?.lastName || '');
    setFirstNameError(null);
    setLastNameError(null);
    setSaveError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };

  const save = async () => {
    const first = validateProfileName(firstName, 'First name');
    const last = validateProfileName(lastName, 'Last name');
    setFirstNameError(first.error);
    setLastNameError(last.error);
    if (!first.ok || !last.ok) return;
    if (!authToken) {
      setSaveError('Sign in to update your profile.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    // Trimmed values only; email/phone are never sent from here.
    const res = await updateGuardianProfile(authToken, {
      firstName: first.value,
      lastName: last.value,
    });
    setSaving(false);
    if (!res.success) {
      // Show the server's own refusal message (e.g. invalid_name).
      setSaveError(res.message || 'Could not save your profile. Try again.');
      return;
    }
    const nextUser = res.data || { ...profileUser, firstName: first.value, lastName: last.value };
    setProfileUser(nextUser);
    onProfileLoadedRef.current?.(nextUser, entitlements);
    setEditing(false);
  };

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', py: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to chat
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Profile
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="overline" color="text.secondary">
              Account
            </Typography>
            {!editing && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={startEdit}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
            )}
          </Box>

          {editing ? (
            <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={(e) => { e.preventDefault(); save(); }}>
              <Stack spacing={2}>
                <TextField
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={Boolean(firstNameError)}
                  helperText={firstNameError || ' '}
                  inputProps={{ maxLength: NAME_MAX }}
                  disabled={saving}
                  fullWidth
                />
                <TextField
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={Boolean(lastNameError)}
                  helperText={lastNameError || ' '}
                  inputProps={{ maxLength: NAME_MAX }}
                  disabled={saving}
                  fullWidth
                />
                <TextField
                  label="Email"
                  value={profileUser?.email || ''}
                  helperText="Email can't be changed here."
                  disabled
                  fullWidth
                />
                {profileUser?.phone && (
                  <TextField
                    label="Phone"
                    value={profileUser.phone}
                    helperText="Phone can't be changed here."
                    disabled
                    fullWidth
                  />
                )}
                {saveError && <Alert severity="error">{saveError}</Alert>}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button onClick={cancelEdit} disabled={saving} color="inherit">
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: profileUser?.phone ? 0.5 : 2 }}>
                {profileUser?.email || '—'}
              </Typography>
              {profileUser?.phone && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {profileUser.phone}
                </Typography>
              )}
            </>
          )}
          <Divider sx={{ my: 2 }} />

          <Typography variant="overline" color="text.secondary">
            Plan
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, mt: 0.5 }}>
            <Chip
              label={`Plan: ${formatTier(
                entitlements?.plan_slug || entitlements?.tier || profileUser?.tier,
              )}`}
            />
            {entitlements?.trial_active && (
              <Chip
                color="info"
                label={`Trial active${entitlements.trial_ends_at ? ` until ${entitlements.trial_ends_at}` : ''}`}
              />
            )}
          </Box>

          <Typography variant="overline" color="text.secondary">
            Usage today (UTC)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Chip
              label={
                entitlements?.dev_unlimited_reports
                  ? 'Reports left today: unlimited (dev)'
                  : `Reports left today: ${entitlements?.reports_remaining_today ?? '—'}`
              }
            />
            <Chip
              label={`Chat prompts left today: ${entitlements?.prompts_remaining_today ?? '—'}`}
            />
            {entitlements?.chat_daily_cap != null && (
              <Chip
                variant="outlined"
                label={`Chat daily cap: ${entitlements.chat_daily_cap}`}
              />
            )}
            {entitlements?.daily_cap != null && !entitlements?.dev_unlimited_reports && (
              <Chip
                variant="outlined"
                label={`Report daily cap: ${entitlements.daily_cap}`}
              />
            )}
          </Box>
        </Paper>
      )}
      {/* How Guardian talks to you (D5, 2026-10-22): learned style + controls. */}
      {authToken && <GuardianStyleSection authToken={authToken} />}
      {authToken && <AccountDataSection authToken={authToken} onDeleted={onAccountDeleted} />}
    </Box>
  );
}
